import * as React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import {
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import { db } from '../../App/+store/firebase';
import {
  listClasslinkTenants,
  previewClasslinkTenant,
  saveClasslinkSettings,
  runClasslinkSync,
  resolveClasslinkMissing,
} from '../+store/Integrations/Integrations.api';
import { isClasslinkAdmin } from './access';
import SyncPanel from './SyncPanel.component';
import ReviewPanel from './ReviewPanel.component';
import { SUBJECT_LABELS, ID_FIELDS, IGNORE, UNSET, getCurrentRosterYear } from './constants';

const styles = (theme) => ({
  root: {
    width: '100%',
    maxWidth: 900,
  },
  intro: {
    marginBottom: 16,
    color: theme.palette.text.secondary,
  },
  section: {
    padding: 16,
    marginBottom: 16,
  },
  sectionTitle: {
    marginBottom: 8,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  field: {
    minWidth: 260,
    marginRight: 16,
    marginBottom: 8,
  },
  smallField: {
    minWidth: 160,
    marginRight: 16,
    marginBottom: 8,
  },
  actions: {
    marginTop: 12,
    display: 'flex',
    alignItems: 'center',
  },
  secondaryButton: {
    marginLeft: 8,
  },
  error: {
    color: theme.palette.error.main,
    marginTop: 8,
  },
  ok: {
    color: '#2e7d32',
    marginTop: 8,
  },
  muted: {
    color: theme.palette.text.secondary,
  },
  sample: {
    fontFamily: 'monospace',
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
  cellNarrow: {
    whiteSpace: 'nowrap',
  },
  // The run history has seven columns; let it scroll rather than widen the card.
  tableScroll: {
    overflowX: 'auto',
  },
});

const emptyConfig = () => ({
  enabled: false,
  tenantId: '',
  oneRosterApplicationId: '',
  tenantName: '',
  targetYear: '',
  campusMap: {},
  idMap: {},
  subjectOverrides: {},
  writeTeachers: true,
});

// A blocking error and a one-line success note live per section so a failed
// preview doesn't hide a successful save elsewhere on the page.
class Integrations extends React.PureComponent {
  static propTypes = {
    classes: PropTypes.any.isRequired,
    profile: PropTypes.object,
    config: PropTypes.object,
    liveState: PropTypes.object,
    schoolList: PropTypes.object,
    getClasslinkSettings: PropTypes.func.isRequired,
    configSaved: PropTypes.func.isRequired,
    liveStateChanged: PropTypes.func.isRequired,
  };

  state = {
    draft: null,
    tenants: null,
    preview: null,
    busy: '',
    errors: {},
    notes: {},
  };

  componentDidMount() {
    const { config, getClasslinkSettings, profile } = this.props;
    if (config == null) {
      getClasslinkSettings();
    } else {
      this.seedDraft(config);
    }
    this.subscribe(profile);
  }

  componentDidUpdate(prevProps) {
    const { config, profile } = this.props;
    if (config !== prevProps.config && config) {
      this.seedDraft(config);
    }
    if (profile && (!prevProps.profile || prevProps.profile.organizationId !== profile.organizationId)) {
      this.subscribe(profile);
    }
  }

  componentWillUnmount() {
    this.unsubscribe();
  }

  subscribe(profile) {
    this.unsubscribe();
    if (!profile || !profile.organizationId) return;
    const { liveStateChanged } = this.props;
    this.liveRef = db.ref(`newDatabase/organizations/${profile.organizationId}/integrations/classlink`);
    this.liveHandler = this.liveRef.on(
      'value',
      (snapshot) => liveStateChanged(snapshot.val() || {}),
      () => liveStateChanged({})
    );
  }

  unsubscribe() {
    if (this.liveRef && this.liveHandler) {
      this.liveRef.off('value', this.liveHandler);
    }
    this.liveRef = null;
    this.liveHandler = null;
  }

  seedDraft(config) {
    this.setState({ draft: { ...emptyConfig(), ...(config || {}) } });
    if (config && config.tenantId && !this.state.preview) {
      this.loadPreview();
    }
  }

  setError = (section, error) => {
    const message = (error && error.message) || String(error || '');
    this.setState((s) => ({ errors: { ...s.errors, [section]: message }, notes: { ...s.notes, [section]: '' } }));
  };

  setNote = (section, note) => {
    this.setState((s) => ({ notes: { ...s.notes, [section]: note }, errors: { ...s.errors, [section]: '' } }));
  };

  updateDraft = (patch) => {
    this.setState((s) => ({ draft: { ...(s.draft || emptyConfig()), ...patch } }));
  };

  // --- callables -----------------------------------------------------------

  loadTenants = async () => {
    this.setState({ busy: 'tenants' });
    try {
      const result = await listClasslinkTenants();
      this.setState({ tenants: (result.data && result.data.tenants) || [], busy: '' });
      this.setNote('connection', '');
    } catch (error) {
      this.setState({ busy: '' });
      this.setError('connection', error);
    }
  };

  loadPreview = async () => {
    this.setState({ busy: 'preview' });
    try {
      const result = await previewClasslinkTenant();
      const preview = result.data || {};
      this.setState((s) => {
        const draft = { ...(s.draft || emptyConfig()) };
        const campusMap = { ...(draft.campusMap || {}) };
        (preview.orgs || []).forEach((org) => {
          if (campusMap[org.sourcedId] === undefined && org.suggestedCampus) {
            campusMap[org.sourcedId] = org.suggestedCampus;
          }
        });
        draft.campusMap = campusMap;
        return { preview, draft, busy: '' };
      });
      this.setNote('mapping', '');
    } catch (error) {
      this.setState({ busy: '' });
      this.setError('mapping', error);
    }
  };

  save = async (section, keys) => {
    const { draft } = this.state;
    if (!draft) return;
    const patch = {};
    keys.forEach((k) => {
      patch[k] = draft[k];
    });
    if (patch.targetYear === '' || patch.targetYear === undefined) {
      if (keys.includes('targetYear')) patch.targetYear = null;
    } else if (keys.includes('targetYear')) {
      patch.targetYear = Number(patch.targetYear);
    }
    this.setState({ busy: section });
    try {
      const result = await saveClasslinkSettings({ patch });
      this.props.configSaved(result.data);
      this.setState({ busy: '' });
      this.setNote(section, 'Saved.');
    } catch (error) {
      this.setState({ busy: '' });
      this.setError(section, error);
    }
  };

  runSync = async () => {
    this.setState({ busy: 'sync' });
    try {
      await runClasslinkSync();
      this.setState({ busy: '' });
      this.setNote('sync', 'Sync queued. Progress appears below as the run proceeds.');
    } catch (error) {
      this.setState({ busy: '' });
      this.setError('sync', error);
    }
  };

  resolveMissing = async (year, firebaseIds, action) => {
    this.setState({ busy: 'review' });
    try {
      const result = await resolveClasslinkMissing({ year, firebaseIds, action });
      this.setState({ busy: '' });
      const n = (result.data && result.data.resolved) || firebaseIds.length;
      this.setNote('review', `${n} student${n === 1 ? '' : 's'} ${action === 'remove' ? 'removed' : 'kept'}.`);
    } catch (error) {
      this.setState({ busy: '' });
      this.setError('review', error);
    }
  };

  // --- derived --------------------------------------------------------------

  campusChoices() {
    const { schoolList, profile } = this.props;
    return Object.entries(schoolList || {})
      .filter(([campusNumber]) => /^\d{9}$/.test(campusNumber) && campusNumber !== (profile && profile.districtNumber))
      .map(([campusNumber, school]) => ({ campusNumber, name: (school && school.name) || campusNumber }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  unmappedOrgCount() {
    const { preview, draft } = this.state;
    if (!preview || !draft) return 0;
    return (preview.orgs || []).filter((org) => {
      if (!org.studentCount) return false;
      const v = (draft.campusMap || {})[org.sourcedId];
      return v === undefined || v === '';
    }).length;
  }

  targetYear() {
    const { config } = this.props;
    return (config && config.targetYear) || getCurrentRosterYear();
  }

  // --- render blocks --------------------------------------------------------

  renderStatusLine(section) {
    const { classes } = this.props;
    const { errors, notes } = this.state;
    if (errors[section]) return <Typography className={classes.error}>{errors[section]}</Typography>;
    if (notes[section]) return <Typography className={classes.ok}>{notes[section]}</Typography>;
    return null;
  }

  renderConnection() {
    const { classes, profile } = this.props;
    const { draft, tenants, busy } = this.state;
    const admin = isClasslinkAdmin(profile);
    const currentYear = getCurrentRosterYear();
    const yearOptions = [currentYear, currentYear - 1];
    const tenantOptions = (tenants || []).filter(
      (t) => !t.linkedOrgId || t.linkedOrgId === (profile && profile.organizationId)
    );
    return (
      <Paper className={classes.section}>
        <Typography variant="subtitle1" color="primary" className={classes.sectionTitle}>
          Connection
        </Typography>
        {!admin && (
          <Typography className={classes.muted} gutterBottom>
            {draft.tenantName
              ? `Linked to ClassLink tenant "${draft.tenantName}". ${
                  draft.enabled ? 'Nightly sync is on.' : 'Nightly sync is off.'
                }`
              : 'No ClassLink tenant is linked yet. CoPilot support links the tenant once your district shares CoPilot in Roster Server.'}
          </Typography>
        )}
        <div className={classes.row}>
          {admin ? (
            <TextField
              select
              label="ClassLink tenant"
              className={classes.field}
              value={draft.tenantId || UNSET}
              onChange={(e) => {
                const t = tenantOptions.find((x) => String(x.tenantId) === String(e.target.value));
                this.updateDraft({
                  tenantId: t ? t.tenantId : '',
                  oneRosterApplicationId: t ? t.oneRosterApplicationId : '',
                  tenantName: t ? t.tenantName : '',
                });
              }}
              onFocus={() => {
                if (tenants == null && busy !== 'tenants') this.loadTenants();
              }}
              helperText={
                tenants == null ? 'Click to load tenants sharing with CoPilot' : `${tenantOptions.length} available`
              }
            >
              <MenuItem value={UNSET}>
                <em>Not linked</em>
              </MenuItem>
              {draft.tenantId && !tenantOptions.some((t) => String(t.tenantId) === String(draft.tenantId)) && (
                <MenuItem value={draft.tenantId}>{draft.tenantName || draft.tenantId}</MenuItem>
              )}
              {tenantOptions.map((t) => (
                <MenuItem key={t.tenantId} value={t.tenantId}>
                  {t.tenantName || t.tenantId}
                  {t.status && t.status !== 'active' ? ` (${t.status})` : ''}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <TextField label="ClassLink tenant" className={classes.field} value={draft.tenantName || ''} disabled />
          )}
          <TextField
            select
            label="Roster year"
            className={classes.smallField}
            value={draft.targetYear || UNSET}
            onChange={(e) => this.updateDraft({ targetYear: e.target.value })}
            helperText={`Default: ${currentYear}-${currentYear + 1}`}
          >
            <MenuItem value={UNSET}>
              <em>
                Current ({currentYear}-{currentYear + 1})
              </em>
            </MenuItem>
            {yearOptions.map((y) => (
              <MenuItem key={y} value={y}>
                {y}-{y + 1}
              </MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!draft.enabled}
                disabled={!admin}
                onChange={(e) => this.updateDraft({ enabled: e.target.checked })}
              />
            }
            label="Nightly sync enabled"
          />
        </div>
        <div className={classes.actions}>
          <Button
            variant="contained"
            color="primary"
            disabled={busy === 'connection'}
            onClick={() =>
              this.save(
                'connection',
                admin ? ['tenantId', 'oneRosterApplicationId', 'tenantName', 'enabled', 'targetYear'] : ['targetYear']
              )
            }
          >
            Save connection
          </Button>
          {admin && (
            <Button className={classes.secondaryButton} disabled={busy === 'tenants'} onClick={this.loadTenants}>
              Refresh tenant list
            </Button>
          )}
        </div>
        {this.renderStatusLine('connection')}
      </Paper>
    );
  }

  renderCampusMapping() {
    const { classes, config } = this.props;
    const { draft, preview, busy } = this.state;
    const choices = this.campusChoices();
    const linked = !!(config && config.tenantId);
    const orgs = (preview && preview.orgs) || [];
    const unmapped = this.unmappedOrgCount();
    return (
      <Paper className={classes.section}>
        <Typography variant="subtitle1" color="primary" className={classes.sectionTitle}>
          Campus mapping
        </Typography>
        <Typography className={classes.intro}>
          Match each ClassLink school to the CoPilot campus it feeds. Students at an unmapped school are skipped.
        </Typography>
        {!linked && <Typography className={classes.muted}>Link a tenant first.</Typography>}
        {linked && !preview && (
          <Button variant="outlined" disabled={busy === 'preview'} onClick={this.loadPreview}>
            {busy === 'preview' ? 'Loading ClassLink schools...' : 'Load ClassLink schools'}
          </Button>
        )}
        {linked && preview && (
          <Table padding="dense">
            <TableHead>
              <TableRow>
                <TableCell>ClassLink school</TableCell>
                <TableCell>Identifier</TableCell>
                <TableCell>Students</TableCell>
                <TableCell>CoPilot campus</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orgs.map((org) => {
                const raw = (draft.campusMap || {})[org.sourcedId];
                const value = raw === null || raw === false ? IGNORE : raw || UNSET;
                return (
                  <TableRow key={org.sourcedId}>
                    <TableCell>
                      {org.name || org.sourcedId}
                      {org.type ? <span className={classes.muted}> ({org.type})</span> : null}
                    </TableCell>
                    <TableCell className={classes.sample}>{org.identifier || ''}</TableCell>
                    <TableCell>{org.studentCount != null ? org.studentCount : ''}</TableCell>
                    <TableCell>
                      <TextField
                        select
                        value={value}
                        style={{ minWidth: 240 }}
                        onChange={(e) => {
                          const v = e.target.value;
                          this.updateDraft({
                            campusMap: {
                              ...(draft.campusMap || {}),
                              [org.sourcedId]: v === IGNORE ? false : v === UNSET ? undefined : v,
                            },
                          });
                        }}
                      >
                        <MenuItem value={UNSET}>
                          <em>Not mapped</em>
                        </MenuItem>
                        <MenuItem value={IGNORE}>Ignore this school</MenuItem>
                        {choices.map((c) => (
                          <MenuItem key={c.campusNumber} value={c.campusNumber}>
                            {c.name} ({c.campusNumber})
                          </MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
        {linked && preview && (
          <div className={classes.actions}>
            <Button
              variant="contained"
              color="primary"
              disabled={busy === 'mapping' || unmapped > 0}
              onClick={() => this.save('mapping', ['campusMap'])}
            >
              Save campus mapping
            </Button>
            <Button className={classes.secondaryButton} disabled={busy === 'preview'} onClick={this.loadPreview}>
              Reload
            </Button>
            {unmapped > 0 && (
              <Typography className={classes.error} style={{ marginTop: 0, marginLeft: 16 }}>
                {unmapped} school{unmapped === 1 ? '' : 's'} with students still need a campus or Ignore.
              </Typography>
            )}
          </div>
        )}
        {this.renderStatusLine('mapping')}
      </Paper>
    );
  }

  renderIdMapping() {
    const { classes, config } = this.props;
    const { draft, preview, busy } = this.state;
    const linked = !!(config && config.tenantId);
    const idTypes = (preview && preview.idTypes) || [];
    const identifierSample = (preview && preview.identifierSample) || [];
    const metaKeys = (preview && preview.metadataKeys) || [];
    const valueFor = (spec) => {
      if (!spec || !spec.source) return UNSET;
      if (spec.source === 'userIds') return `userIds:${spec.type || ''}`;
      if (spec.source === 'metadata') return `metadata:${spec.key || ''}`;
      return spec.source;
    };
    const specFor = (value) => {
      if (!value) return undefined;
      if (value.startsWith('userIds:')) return { source: 'userIds', type: value.slice('userIds:'.length) };
      if (value.startsWith('metadata:')) return { source: 'metadata', key: value.slice('metadata:'.length) };
      return { source: value };
    };
    return (
      <Paper className={classes.section}>
        <Typography variant="subtitle1" color="primary" className={classes.sectionTitle}>
          ID mapping
        </Typography>
        <Typography className={classes.intro}>
          Which ClassLink identifier feeds each CoPilot ID. Leave a field on Default to use CoPilot&apos;s best guess.
          STUDENT-ID must be the TEA state ID; students without one are skipped.
        </Typography>
        {!linked && <Typography className={classes.muted}>Link a tenant first.</Typography>}
        {linked && !preview && (
          <Typography className={classes.muted}>Load ClassLink schools above to see the available ID types.</Typography>
        )}
        {linked && preview && (
          <div className={classes.row}>
            {ID_FIELDS.map((f) => (
              <TextField
                key={f.key}
                select
                label={f.label}
                className={classes.field}
                value={valueFor((draft.idMap || {})[f.key])}
                onChange={(e) =>
                  this.updateDraft({ idMap: { ...(draft.idMap || {}), [f.key]: specFor(e.target.value) } })
                }
              >
                <MenuItem value={UNSET}>
                  <em>Default</em>
                </MenuItem>
                <MenuItem value="identifier">
                  identifier{identifierSample.length ? ` (e.g. ${identifierSample.slice(0, 2).join(', ')})` : ''}
                </MenuItem>
                {idTypes.map((t) => (
                  <MenuItem key={t.type} value={`userIds:${t.type}`}>
                    userIds: {t.type}
                    {t.sample && t.sample.length ? ` (e.g. ${t.sample.slice(0, 2).join(', ')})` : ''}
                  </MenuItem>
                ))}
                {metaKeys.map((m) => (
                  <MenuItem key={m.key} value={`metadata:${m.key}`}>
                    metadata: {m.key}
                    {m.sample && m.sample.length ? ` (e.g. ${m.sample.slice(0, 2).join(', ')})` : ''}
                  </MenuItem>
                ))}
                <MenuItem value="username">username</MenuItem>
                <MenuItem value="email">email</MenuItem>
                <MenuItem value="none">Not provided</MenuItem>
              </TextField>
            ))}
          </div>
        )}
        {linked && preview && (
          <div className={classes.actions}>
            <Button
              variant="contained"
              color="primary"
              disabled={busy === 'ids'}
              onClick={() => this.save('ids', ['idMap'])}
            >
              Save ID mapping
            </Button>
          </div>
        )}
        {this.renderStatusLine('ids')}
      </Paper>
    );
  }

  renderSubjects() {
    const { classes, liveState } = this.props;
    const { draft, busy } = this.state;
    const year = this.targetYear();
    const pending = (liveState && liveState.pending && liveState.pending[year]) || {};
    const unmatched = Object.entries(pending.unmatchedClasses || {});
    const overrides = draft.subjectOverrides || {};
    const overridden = Object.entries(overrides).filter(([k]) => !unmatched.some(([id]) => id === k));
    // Written by the sync for every class with a saved subject or Ignore (see buildRoster).
    const overriddenDetails = pending.overriddenClasses || {};
    const setOverride = (key, value) => {
      const next = { ...overrides };
      if (value === UNSET) delete next[key];
      else next[key] = value === IGNORE ? false : value;
      this.updateDraft({ subjectOverrides: next });
    };
    return (
      <Paper className={classes.section}>
        <Typography variant="subtitle1" color="primary" className={classes.sectionTitle}>
          Subjects
        </Typography>
        <Typography className={classes.intro}>
          Classes CoPilot could not match to a STAAR subject after the last sync. Pick the subject, or Ignore.
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={draft.writeTeachers !== false}
              onChange={(e) => this.updateDraft({ writeTeachers: e.target.checked })}
            />
          }
          label="Fill teacher names from class enrollments"
        />
        {!unmatched.length && !overridden.length && (
          <Typography className={classes.muted}>Nothing to review.</Typography>
        )}
        {(unmatched.length > 0 || overridden.length > 0) && (
          <Table padding="dense">
            <TableHead>
              <TableRow>
                <TableCell>Class</TableCell>
                <TableCell>Course code</TableCell>
                <TableCell>Grades</TableCell>
                <TableCell>Students</TableCell>
                <TableCell>Subject</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {unmatched.map(([id, cls]) => (
                <TableRow key={id}>
                  <TableCell>
                    {cls.title || id}
                    {cls.subjects && cls.subjects.length ? (
                      <span className={classes.muted}> ({cls.subjects.join(', ')})</span>
                    ) : null}
                  </TableCell>
                  <TableCell className={classes.sample}>{cls.courseCode || ''}</TableCell>
                  <TableCell>{(cls.grades || []).join(', ')}</TableCell>
                  <TableCell>{cls.studentCount != null ? cls.studentCount : ''}</TableCell>
                  <TableCell>{this.renderSubjectSelect(overrides, id, setOverride)}</TableCell>
                </TableRow>
              ))}
              {overridden.map(([key, value]) => {
                const cls = overriddenDetails[key];
                return (
                  <TableRow key={key}>
                    <TableCell>
                      {cls && cls.title ? cls.title : <span className={classes.sample}>{key}</span>}
                      {cls && cls.classCount > 1 ? (
                        <span className={classes.muted}> and {cls.classCount - 1} more</span>
                      ) : null}
                      {cls && cls.subjects && cls.subjects.length ? (
                        <span className={classes.muted}> ({cls.subjects.join(', ')})</span>
                      ) : null}
                      {!cls ? <span className={classes.muted}> (not in the last sync)</span> : null}
                    </TableCell>
                    <TableCell className={classes.sample}>{(cls && cls.courseCode) || ''}</TableCell>
                    <TableCell>{cls ? (cls.grades || []).join(', ') : ''}</TableCell>
                    <TableCell>{cls && cls.studentCount != null ? cls.studentCount : ''}</TableCell>
                    <TableCell>{this.renderSubjectSelect(overrides, key, setOverride, value)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
        <div className={classes.actions}>
          <Button
            variant="contained"
            color="primary"
            disabled={busy === 'subjects'}
            onClick={() => this.save('subjects', ['subjectOverrides', 'writeTeachers'])}
          >
            Save subjects
          </Button>
        </div>
        {this.renderStatusLine('subjects')}
      </Paper>
    );
  }

  renderSubjectSelect(overrides, key, setOverride) {
    const has = Object.prototype.hasOwnProperty.call(overrides, key);
    const value = !has ? UNSET : overrides[key] === null || overrides[key] === false ? IGNORE : overrides[key];
    return (
      <TextField select value={value} style={{ minWidth: 180 }} onChange={(e) => setOverride(key, e.target.value)}>
        <MenuItem value={UNSET}>
          <em>Unmatched</em>
        </MenuItem>
        <MenuItem value={IGNORE}>Ignore</MenuItem>
        {Object.entries(SUBJECT_LABELS).map(([k, label]) => (
          <MenuItem key={k} value={k}>
            {label}
          </MenuItem>
        ))}
      </TextField>
    );
  }

  render() {
    const { classes, config, liveState, schoolList, profile } = this.props;
    const { draft, busy } = this.state;
    if (!draft || config == null) {
      return <Typography className={classes.muted}>Loading ClassLink settings...</Typography>;
    }
    const year = this.targetYear();
    return (
      <div className={classes.root}>
        <Typography variant="h6" gutterBottom>
          ClassLink rostering
        </Typography>
        <Typography className={classes.intro}>
          Names, IDs, campus, grade, demographics and class teachers come from ClassLink nightly. Program flags (EcoDis,
          SpEd, EL, enrollment, Highly Mobile) still come from the roster CSV.
        </Typography>
        {this.renderConnection()}
        {this.renderCampusMapping()}
        {this.renderIdMapping()}
        {this.renderSubjects()}
        <SyncPanel
          classes={classes}
          config={config}
          liveState={liveState}
          busy={busy === 'sync'}
          onRun={this.runSync}
          statusLine={this.renderStatusLine('sync')}
        />
        <ReviewPanel
          classes={classes}
          year={year}
          liveState={liveState}
          schoolList={schoolList}
          profile={profile}
          busy={busy === 'review'}
          onResolve={this.resolveMissing}
          statusLine={this.renderStatusLine('review')}
        />
      </div>
    );
  }
}

export default withStyles(styles)(Integrations);
